import React, { memo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import FloatingHeader from '../components/layout/FloatingHeader';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import { useInstaller } from '../components/InstallerDownload';
import { texte, PANELS } from '../components/startseite/texte';
import { starteAbschnitte } from '../components/startseite/abschnitte';
import { InstallerFenster, InstallerKnoepfe } from '../components/startseite/StartAbschnitte';
import Lichtszene from '../components/licht/Lichtszene';
import '../components/startseite/startseite.css';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';

// Installer-Seite (/installer): erklärt den kostenlosen David Media Installer — laden, öffnen (Mac und
// Windows), anmelden, installieren, in After Effects öffnen. Fakten: Mac-Build nur Apple Silicon
// (GraphFlow-Installer/veroeffentlichen.sh, arm64), After Effects ab 2019 (Host AEFT 16.0 im Manifest).

const ZEICHEN = {
  ort: <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>,
  update: <svg viewBox="0 0 24 24"><path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" /></svg>,
  gratis: <svg viewBox="0 0 24 24"><path d="M20 12v8H4v-8" /><rect x="2" y="7" width="20" height="5" rx="1" /><path d="M12 22V7" /><path d="M12 7c-2-4-6-4-6-1.5S10 7 12 7zM12 7c2-4 6-4 6-1.5S14 7 12 7z" /></svg>,
  konto: <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></svg>,
};

function Texte(en) {
  const de = !en;
  return {
    kicker: de ? 'Kostenlos · Mac & Windows' : 'Free · Mac & Windows',
    h1a: 'David Media Installer',
    h1b: de ? 'Einmal installieren. Alles an einem Ort.' : 'Install once. Everything in one place.',
    lead: de
      ? 'Der Installer richtet alles ein, was du besitzt, hält es aktuell und bringt die Gratis-Sachen gleich mit. Einzelkauf, All-in-One oder nur die kostenlosen Presets: Er ist für alle kostenlos.'
      : 'The installer sets up everything you own, keeps it up to date and brings the free items along. Single purchase, All-in-One or just the free presets: it’s free for everyone.',
    schritteKicker: de ? "So geht's" : 'How it works',
    schritteH2a: de ? 'Fünf Schritte.' : 'Five steps.',
    schritteH2b: de ? 'In ein paar Minuten fertig.' : 'Done in a few minutes.',
    schritte: de ? [
      ['Herunterladen', <>Oben <b>Für Mac laden</b> oder <b>Für Windows laden</b> wählen. Der Mac-Installer läuft auf Macs mit Apple-Chip (M1 oder neuer).</>],
      ['Öffnen', <>Mac: die Datei öffnen und den Installer in den <b>Programme</b>-Ordner ziehen. Windows: das Setup starten.</>],
      ['Anmelden', <>Mit deinem David-Media-Konto anmelden. Das Konto ist kostenlos.</>],
      ['Installieren', <>Unter <b>Tools</b> siehst du alles, was du besitzt. Ein Klick installiert es, die Gratis-Sachen liegen unter <b>Assets</b>.</>],
      ['In After Effects öffnen', <>After Effects neu starten, dann <b>Fenster › Erweiterungen</b>.</>],
    ] : [
      ['Download', <>Choose <b>Download for Mac</b> or <b>Download for Windows</b> above. The Mac installer runs on Macs with Apple silicon (M1 or newer).</>],
      ['Open', <>Mac: open the file and drag the installer into <b>Applications</b>. Windows: run the setup.</>],
      ['Sign in', <>Sign in with your David Media account. The account is free.</>],
      ['Install', <>Under <b>Tools</b> you see everything you own. One click installs it, the free items are under <b>Assets</b>.</>],
      ['Open in After Effects', <>Restart After Effects, then <b>Window › Extensions</b>.</>],
    ],
    kannKicker: de ? 'Was er kann' : 'What it does',
    kannH2a: de ? 'Dein Zuhause für alle Werkzeuge.' : 'Home for all your tools.',
    kannH2b: de ? 'Statt Dateien hin und her zu schieben.' : 'Instead of shuffling files around.',
    kann: de ? [
      ['ort', 'Alles an einem Ort', 'Jedes Werkzeug, das du besitzt, mit einem Klick installiert. Kein Suchen nach Dateien, kein Kopieren in Ordner.'],
      ['update', 'Updates mit einem Klick', 'Neue Versionen zeigt der Installer selbst an, „Alle aktualisieren“ holt sie. Er hält sich auch selbst aktuell.'],
      ['gratis', 'Gratis-Sachen inklusive', 'Text-Animationen, Verläufe und das Flow Panel liegen direkt drin, kostenlos mit deinem Konto.'],
      ['konto', 'Dein Konto, deine Werkzeuge', 'Einzelkauf oder All-in-One: Nach der Anmeldung siehst du genau das, was zu deinem Konto gehört.'],
    ] : [
      ['ort', 'Everything in one place', 'Every tool you own, installed in one click. No hunting for files, no copying into folders.'],
      ['update', 'One-click updates', 'The installer shows new versions itself and “Update all” fetches them. It keeps itself up to date too.'],
      ['gratis', 'Free items included', 'Text animations, gradients and the Flow Panel are right inside, free with your account.'],
      ['konto', 'Your account, your tools', 'Single purchase or All-in-One: after signing in you see exactly what belongs to your account.'],
    ],
    faq: de ? [
      ['Was kostet der Installer?', 'Nichts. Er ist für alle kostenlos, auch ohne Kauf. Die Gratis-Sachen und das Flow Panel bekommst du damit ebenfalls.'],
      ['Auf welchen Rechnern läuft er?', 'Auf Macs mit Apple-Chip (M1 oder neuer) und auf Windows. Die Werkzeuge laufen in After Effects ab Version 2019.'],
      ['Warum warnt Windows beim Start?', 'Der Installer ist noch nicht von Microsoft signiert, deshalb zeigt Windows beim ersten Start einen Hinweis. Auf „Weitere Informationen“ und dann „Trotzdem ausführen“ klicken. Die Signatur holen wir nach.'],
      ['Wie bekomme ich Updates?', 'Der Installer zeigt an, wenn es neue Versionen gibt. „Alle aktualisieren“ holt sie mit einem Klick, danach After Effects neu starten.'],
      ['Wie entferne ich ein Werkzeug?', 'Im Installer beim Werkzeug auf „Entfernen“ klicken.'],
      ['Geht es auch ohne Installer?', 'Ja. Jede Erweiterung gibt es nach dem Kauf auch einzeln als .zxp-Datei in deinem Konto, die Gratis-Pakete direkt als .zip auf der Website.'],
    ] : [
      ['What does the installer cost?', 'Nothing. It’s free for everyone, even without a purchase. You also get the free items and the Flow Panel with it.'],
      ['Which computers does it run on?', 'Macs with Apple silicon (M1 or newer) and Windows. The tools run in After Effects 2019 or newer.'],
      ['Why does Windows show a warning?', 'The installer isn’t Microsoft-signed yet, so Windows shows a notice on first launch. Click “More info”, then “Run anyway”. We’re working on the signature.'],
      ['How do I get updates?', 'The installer shows when new versions are available. “Update all” fetches them in one click, then restart After Effects.'],
      ['How do I remove a tool?', 'In the installer, click “Remove” next to the tool.'],
      ['Can I do without the installer?', 'Yes. Every extension is also available as a single .zxp file in your account after purchase, and the free packages as a .zip on the website.'],
    ],
  };
}

const Inhalt = memo(function Inhalt({ en, version }) {
  const T = texte(en);
  const I = Texte(en);
  const p = en ? '/en' : '';
  return (
    <>
      <section className="sek erste voll" id="installer">
        <div className="innen inst">
          <InstallerFenster T={T} oben />
          <div className="reveal-text">
            <span className="kicker">{I.kicker}</span>
            <h1 className="als-h2"><span className="zl"><span>{I.h1a}</span></span><span className="zl"><span className="grau">{I.h1b}</span></span></h1>
            <p className="lead reveal">{I.lead}</p>
            {version && <p className="versionszeile"><span>Version {version}</span> <span>· macOS (Apple Silicon)</span> <span>· Windows</span></p>}
            <InstallerKnoepfe t={T.inst} en={en} />
          </div>
        </div>
      </section>

      <section className="sek" id="schritte">
        <div className="innen">
          <div className="zeitlinie"><b /><i /><span>00:00:01:00</span></div>
          <div className="kopf">
            <div className="reveal-text">
              <span className="kicker">{I.schritteKicker}</span>
              <h2><span className="zl"><span>{I.schritteH2a}</span></span><span className="zl"><span className="grau">{I.schritteH2b}</span></span></h2>
            </div>
          </div>
          <div className="zeitstrahl reveal" style={{ '--n': I.schritte.length }}>
            <div className="linie"><b /></div>
            {I.schritte.map(([titel, text], i) => (
              <div className="punkt" key={titel} style={{ '--i': i }}>
                <i className="raute" /><span className="tc">00:00:0{i + 1}:00</span><h3>{titel}</h3><p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sek" id="kann">
        <div className="innen">
          <div className="zeitlinie"><b /><i /><span>00:00:02:00</span></div>
          <div className="kopf">
            <div className="reveal-text">
              <span className="kicker">{I.kannKicker}</span>
              <h2><span className="zl"><span>{I.kannH2a}</span></span><span className="zl"><span className="grau">{I.kannH2b}</span></span></h2>
            </div>
          </div>
          <div className="funktionen">
            {I.kann.map(([zeichen, titel, text], i) => (
              <div className="funktion reveal" key={titel} style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="zeichen">{ZEICHEN[zeichen]}</div>
                <h3>{titel}</h3><p>{text}</p>
              </div>
            ))}
          </div>
          <div className="weiter reveal">
            <Link className="btn glas" to={en ? '/en/free' : '/kostenlos'}>{en ? 'See the free items' : 'Gratis-Sachen ansehen'} <span aria-hidden="true">→</span></Link>
            <Link className="btn glas" to={`${p}/shop`}>{en ? 'See all tools' : 'Alle Werkzeuge ansehen'} <span aria-hidden="true">→</span></Link>
          </div>
        </div>
      </section>

      <section className="sek" id="fragen">
        <div className="innen">
          <div className="zeitlinie"><b /><i /><span>00:00:03:00</span></div>
          <div className="kopf">
            <div className="reveal-text">
              <span className="kicker">{en ? 'Questions' : 'Fragen'}</span>
              <h2><span className="zl"><span>{en ? 'Good to know.' : 'Gut zu wissen.'}</span></span></h2>
            </div>
          </div>
          <div className="faq">
            {I.faq.map(([f, a], i) => <details key={f} open={i === 0}><summary>{f}</summary><p>{a}</p></details>)}
          </div>
        </div>
      </section>
    </>
  );
});

export default function Installer() {
  const location = useLocation();
  const en = location.pathname.startsWith('/en/') || location.pathname === '/en';
  const wurzel = useRef(null);
  const info = useInstaller();

  useEffect(() => {
    const T = texte(en);
    return starteAbschnitte({ wurzel: wurzel.current, T: T.szene, panels: PANELS, preise: T.preise });
  }, [en]);

  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title={en ? 'David Media Installer — free for Mac & Windows | DAVIDMEDIA' : 'David Media Installer — kostenlos für Mac & Windows | DAVIDMEDIA'}
        description={en
          ? 'The free David Media Installer sets up every tool you own for After Effects, keeps it up to date and includes the free presets. Mac (Apple silicon) and Windows.'
          : 'Der kostenlose David Media Installer richtet alle deine After-Effects-Werkzeuge ein, hält sie aktuell und bringt die Gratis-Presets mit. Mac (Apple-Chip) und Windows.'}
        url={en ? 'https://www.davidmedia.de/en/installer' : 'https://www.davidmedia.de/installer'}
      />
      <FloatingHeader />
      <div className="startseite" ref={wurzel}>
        <Lichtszene />
        <Inhalt en={en} version={info ? info.version : null} />
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </motion.div>
  );
}
